import { useMemo } from 'react';
import {
  DbLocation,
  useDeleteApiLocationsByIdMutation,
  useGetApiLocationsQuery,
  useGetApiLocationTypesQuery,
  usePatchApiLocationsByIdMutation,
  usePostApiLocationsMutation,
} from '../../../../app/athenaApiGen';
import AdminCatalogPanel, { AdminCatalogSelectOption } from '../AdminCatalogPanel';

// Location master catalog (Pattern A). Each row points at a LocationType
// via the `locationType` FK (string, not UUID — LocationType's PK is its
// own name). The FK is required on create but mutable on update — server
// rejects deletion of a LocationType while any Location still references
// it (handled by AdminLocationTypesPanel's fkConflictMessage).
const AdminLocationsPanel = () => {
  // LocationType options. Returns straight array (no envelope).
  const { data: locationTypes } = useGetApiLocationTypesQuery({});
  const typeOptions = useMemo<AdminCatalogSelectOption[]>(
    () =>
      (locationTypes ?? [])
        .filter((t) => !t.archived)
        .map((t) => ({
          value: t.locationType ?? '',
          label: t.description
            ? `${t.locationType} — ${t.description}`
            : (t.locationType ?? ''),
        })),
    [locationTypes],
  );

  return (
    <AdminCatalogPanel<DbLocation>
      tableName="Location"
      apiPath="/api/locations"
      pkAccessor="locationId"
      useList={useGetApiLocationsQuery}
      useCreate={usePostApiLocationsMutation}
      useUpdate={usePatchApiLocationsByIdMutation}
      useDelete={useDeleteApiLocationsByIdMutation}
      buildCreateArg={(v) => ({
        handlersCreateLocationRequest: {
          name: (v.name as string) ?? '',
          locationType: (v.locationType as string) ?? '',
          locationCode: (v.locationCode as string) || undefined,
          locationAddress: (v.locationAddress as string) || undefined,
          locationCity: (v.locationCity as string) || undefined,
          locationState: (v.locationState as string) || undefined,
          locationZip: (v.locationZip as string) || undefined,
          description: (v.description as string) || undefined,
        },
      })}
      buildUpdateArg={(row, v) => ({
        id: row.locationId as string,
        handlersUpdateLocationRequest: {
          name: (v.name as string) || undefined,
          locationType: (v.locationType as string) || undefined,
          locationCode: (v.locationCode as string) || undefined,
          locationAddress: (v.locationAddress as string) || undefined,
          locationCity: (v.locationCity as string) || undefined,
          locationState: (v.locationState as string) || undefined,
          locationZip: (v.locationZip as string) || undefined,
          description: (v.description as string) || undefined,
        },
      })}
      buildDeleteArg={(row) => ({ id: row.locationId as string })}
      fields={[
        { name: 'name', label: 'Name', type: 'text', required: true },
        {
          name: 'locationType',
          label: 'Type',
          type: 'select',
          required: true,
          options: typeOptions,
          helperText: 'Pick from the LocationType catalog.',
        },
        { name: 'locationCode', label: 'Code', type: 'text' },
        {
          name: 'locationAddress',
          label: 'Address',
          type: 'text',
          hideInGrid: true,
        },
        { name: 'locationCity', label: 'City', type: 'text' },
        { name: 'locationState', label: 'State', type: 'text' },
        { name: 'locationZip', label: 'ZIP', type: 'text' },
        {
          name: 'description',
          label: 'Description',
          type: 'textarea',
          hideInGrid: true,
        },
      ]}
      fkConflictMessage="One or more ProgramLocation rows still reference this Location. Remove those program associations before deleting."
    />
  );
};

export default AdminLocationsPanel;
